// components/GastSteinListe.js
// Angepasste Version von SteinListe.js für Gast-Benutzer

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams as useReactRouterSearchParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import API from '../services/api';
import Modal from './Modal';
import SteinBild from './SteinBild';
import '../styles/SteinListe.css';
import '../styles/SearchForm.css';
import '../styles/Sort.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const KATEGORIEN = ['Einzelscheiben', 'Serien', 'Längsschnitte', 'Kristalle'];

const FILTER_STORAGE_KEY = 'gastSteinListeFilters';

const getParamsObject = (searchParams) => Object.fromEntries(searchParams.entries());

const defaultParams = {
    search: '', farbe: '', kategorie: '', merkmal: '',
    status: '',
    sortBy: 'name', sortOrder: 'asc', page: '1', limit: '0',
};

function GastSteinListe() {
    const [steine, setSteine] = useState([]);
    const [totalSteine, setTotalSteine] = useState(0);
    // const userRole = localStorage.getItem('role') || 'guest'; // Ist hier immer 'guest'
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useReactRouterSearchParams();

    const currentParams = useMemo(() => ({
        ...defaultParams,
        ...getParamsObject(searchParams),
    }), [searchParams]);

    const [showModal, setShowModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    // const [showFilter, setShowFilter] = useState(false); // Filter für Gäste standardmäßig aus oder nicht vorhanden

    const [farbenOptions, setFarbenOptions] = useState([]);

    const isRestoringScroll = useRef(sessionStorage.getItem('restoreScroll') === 'true');
    const prevParamsRef = useRef(currentParams);
    // Ref to ensure filter restoration only runs once on mount
    const didRestoreFilters = useRef(false);

    // --- Fetching Data ---

    const fetchFilterOptions = useCallback(() => {
        API.get('/steine/farben')
            .then((res) => setFarbenOptions(res.data.filter(Boolean).sort()))
            .catch((err) => console.error('Fehler beim Laden der Farben (Gast):', err));
    }, []);

    const fetchSteine = useCallback((paramsToUse) => {
        setIsLoading(true);
        setError(null);
        const query = new URLSearchParams(paramsToUse).toString();
        console.log("Fetching steine for GUEST with query:", query);

        API.get(`/steine/search?${query}`) // Backend berücksichtigt Gast-Rolle automatisch
            .then((res) => {
                setSteine(res.data.steine || []);
                setTotalSteine(res.data.total || 0);
            })
            .catch((err) => {
                console.error("Fehler beim Laden der Steine (Gast):", err);
                if (err.response && (err.response.status === 401 || (err.response.status === 400 && err.response.data?.message === 'Ungültiges oder abgelaufenes Token.') || (err.response.status === 400 && err.response.data === 'Ungültiges Token'))) {
                    setError('Sitzung abgelaufen oder Token ungültig. Bitte neu anmelden.');
                } else {
                    setError('Fehler beim Laden der Steine. Bitte erneut versuchen.');
                }
                setSteine([]);
                setTotalSteine(0);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(debounce((params) => {
        fetchSteine(params);
    }, 350), [fetchSteine]);

    useEffect(() => {
        isRestoringScroll.current = sessionStorage.getItem('restoreScroll') === 'true';
        debouncedFetch(currentParams);
        return () => {
            debouncedFetch.cancel();
        };
    }, [currentParams, debouncedFetch]);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // --- Filter-State Session-Persistenz ---
    // Einmalig beim Mount: URL-Parameter aus sessionStorage wiederherstellen,
    // falls die URL keine Filter enthält (z.B. nach navigate('/') aus GastSteinDetail).
    useEffect(() => {
        if (didRestoreFilters.current) return;
        didRestoreFilters.current = true;
        const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
        if (stored && !searchParams.toString()) {
            setSearchParams(new URLSearchParams(stored), { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Aktuelle URL-Parameter in sessionStorage spiegeln
    useEffect(() => {
        const queryString = searchParams.toString();
        if (queryString) {
            sessionStorage.setItem(FILTER_STORAGE_KEY, queryString);
        }
    }, [searchParams]);

    // --- Scroll Restoration Logic (kann meist so bleiben) ---
    useEffect(() => {
        if (!isLoading && isRestoringScroll.current) {
            const position = sessionStorage.getItem('scrollPosition');
            const stonesAvailable = Array.isArray(steine) && steine.length > 0;
            if (position != null && stonesAvailable) {
                requestAnimationFrame(() => {
                    window.scrollTo({ top: parseInt(position, 10), behavior: 'instant' });
                    sessionStorage.removeItem('scrollPosition');
                    sessionStorage.removeItem('restoreScroll');
                    isRestoringScroll.current = false;
                });
            } else {
                sessionStorage.removeItem('scrollPosition');
                sessionStorage.removeItem('restoreScroll');
                isRestoringScroll.current = false;
            }
        }
    }, [isLoading, steine]);

    // --- Scroll To Top on Filter/Page/Sort Change (kann meist so bleiben) ---
     useEffect(() => {
        const paramsJustChanged = prevParamsRef.current !== currentParams;
        const shouldRestore = sessionStorage.getItem('restoreScroll') === 'true';

        const relevantParamsChanged = (
            prevParamsRef.current.page !== currentParams.page ||
            prevParamsRef.current.sortBy !== currentParams.sortBy ||
            prevParamsRef.current.sortOrder !== currentParams.sortOrder ||
            prevParamsRef.current.limit !== currentParams.limit ||
            prevParamsRef.current.search !== currentParams.search ||
            prevParamsRef.current.farbe !== currentParams.farbe ||
            prevParamsRef.current.kategorie !== currentParams.kategorie ||
            prevParamsRef.current.merkmal !== currentParams.merkmal
        );

        if (relevantParamsChanged && !shouldRestore) {
            const listElement = document.querySelector('.stones-list-section'); // ID anpassen falls nötig
            if (listElement) {
                 window.scrollTo({ top: listElement.offsetTop - 80, behavior: 'instant' });
            }
        }
        if (paramsJustChanged) {
             prevParamsRef.current = currentParams;
        }
    }, [currentParams]);


    // --- Event Handlers ---
    const updateSearchParams = (newValues) => {
        const updated = new URLSearchParams(searchParams);
        let changed = false;
        let filtersChanged = false;

        for (const key in newValues) {
            const oldValue = updated.get(key) ?? defaultParams[key];
            const newValue = String(newValues[key] ?? '');
            const effectiveOldValue = String(oldValue ?? '');

            if (newValue !== effectiveOldValue) {
                 const isFilterKey = !['page', 'limit', 'sortBy', 'sortOrder'].includes(key);
                if (newValue === '' || newValue === String(defaultParams[key] ?? '')) {
                    if (updated.has(key)) {
                        updated.delete(key);
                        changed = true;
                        if(isFilterKey) filtersChanged = true;
                    }
                } else {
                    updated.set(key, newValue);
                    changed = true;
                    if(isFilterKey) filtersChanged = true;
                }
            }
        }

        if (changed && filtersChanged && updated.get('page') !== '1') {
            updated.set('page', '1');
        } else if (changed && !filtersChanged && newValues.hasOwnProperty('limit') && updated.get('page') !== '1') {
             updated.set('page', '1');
        }

        if (changed) {
            sessionStorage.removeItem('restoreScroll');
            sessionStorage.removeItem('scrollPosition');
            isRestoringScroll.current = false;
            setSearchParams(updated, { replace: true });
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target; // Checkboxen für Gäste ggf. nicht relevant
        updateSearchParams({ [name]: value });
    };

    const handleStatusFilterClick = (status) => { // Ggf. für Gäste relevant
        updateSearchParams({ status: currentParams.status === status ? '' : status });
    };
    
    // handleReset für Gäste ggf. vereinfachen oder entfernen, wenn keine komplexen Filter
    const handleResetFilters = () => {
        const resetParams = {};
        for (const key in defaultParams) {
            if (!['limit'].includes(key)) {
                 resetParams[key] = defaultParams[key];
            }
        }
        resetParams.limit = currentParams.limit || defaultParams.limit;

        const resetSearchParams = new URLSearchParams();
        for (const key in resetParams) {
            if (resetParams[key] !== defaultParams[key]) {
                 resetSearchParams.set(key, resetParams[key]);
            }
        }
        if (!resetSearchParams.has('page')) resetSearchParams.set('page', '1');
        if (!resetSearchParams.has('limit')) resetSearchParams.set('limit', defaultParams.limit);
        
        sessionStorage.removeItem('restoreScroll');
        sessionStorage.removeItem('scrollPosition');
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        isRestoringScroll.current = false;
        setSearchParams(resetSearchParams, { replace: true });
        // setShowFilter(false); // Nicht relevant, wenn showFilter entfernt wird
    };


    const saveScrollAndNavigate = (path) => {
        const scrollPosition = window.pageYOffset;
        sessionStorage.setItem('scrollPosition', scrollPosition.toString());
        sessionStorage.setItem('restoreScroll', 'true');
        navigate(path);
    };

    // handleEdit und handleDelete werden für Gäste entfernt

    const handleCardClick = (event, stein) => {
        if (event.target.closest('button, a, input, select, textarea, img')) {
            if (!event.target.closest('img')) {
                 return;
            }
            if (event.target.closest('button, a, input, select, textarea')) return;
        }
         saveScrollAndNavigate(`/steine/${stein._id}`); // Navigiert zu GastSteinDetail
    };
    
    const handleImageClick = (event, stein) => {
        event.stopPropagation();
        // Bildpfad muss in den allowedAttributes des Gastes sein, damit das funktioniert
        if (stein.bildPfad) {
            setSelectedImage(`${process.env.REACT_APP_BASE_URL}/${stein.bildPfad}`);
            setShowModal(true);
        } else {
            // console.warn("Kein Bildpfad für dieses Bild vorhanden (Gast).");
        }
    };

    // --- Sorting (kann meist so bleiben) ---
    const handleSortChange = (e) => {
        const newSortBy = e.target.value;
        const newSortOrder = newSortBy && newSortBy === currentParams.sortBy ? currentParams.sortOrder : 'asc';
        updateSearchParams({ sortBy: newSortBy, sortOrder: newSortOrder });
    };

    const toggleSortOrder = () => {
        if (currentParams.sortBy) {
            updateSearchParams({ sortOrder: currentParams.sortOrder === 'asc' ? 'desc' : 'asc' });
        }
    };

    // --- Pagination (kann meist so bleiben) ---
    const itemsPerPage = parseInt(currentParams.limit, 10) || 0;
    const currentPage = parseInt(currentParams.page, 10) || 1;
    const gesamtSeiten = itemsPerPage === 0 ? 1 : Math.ceil(totalSteine / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= gesamtSeiten && newPage !== currentPage) {
             updateSearchParams({ page: newPage.toString() });
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newLimit = e.target.value;
        updateSearchParams({ limit: newLimit, page: '1' });
    };

    const aktuelleSteine = useMemo(() => steine || [], [steine]);

    // Dynamische Erstellung der Sortieroptionen basierend auf erlaubten Attributen
    // Dies ist eine fortgeschrittenere Idee, für den Anfang statische Liste behalten oder reduzieren.
    // const allowedSortOptions = useMemo(() => {
    // const defaultSortFields = ['name']; // Immer erlauben?
    // if (steine && steine.length > 0 && steine[0]) {
    // return [...new Set([...defaultSortFields, ...Object.keys(steine[0]).filter(key => !['_id', 'bildPfad', 'notizen'].includes(key))])];
    // }
    // return defaultSortFields;
    // }, [steine]);


    return (
        <main>
            {/* Search and Filter Section - Vereinfacht für Gäste */}
            <section className="search-filter-section">
                 <div className="search-bar">
                     <input
                        type="text" id="search" name="search" placeholder="Suche nach Name..." // Notizen für Gäste ggf. nicht durchsuchbar
                        value={currentParams.search} onChange={handleFilterChange} aria-label="Suche nach Name"
                    />
                    {/* Kein "Filter anzeigen/ausblenden" Button für Gäste */}
                </div>
                {/* Status Quick Filters (ggf. beibehalten, wenn Status ein erlaubtes Attribut ist) */}
                {(aktuelleSteine.length > 0 && aktuelleSteine[0]?.hasOwnProperty('status')) && (
                    <div className="status-filter-buttons">
                    {['', 'potenzieller Kauf', 'gekauft', 'verkauft', 'noch keinen Kontakt'].map(statusVal => (
                        <button key={statusVal || 'alle'}
                            className={`status-filter-button ${statusVal.replace(/\s+/g, '-').toLowerCase() || 'all'} ${currentParams.status === statusVal ? 'active' : ''}`}
                            onClick={() => handleStatusFilterClick(statusVal)} > {statusVal || 'Alle'} </button>
                    ))}
                    </div>
                )}
                 {/* Keine Advanced Filters für Gäste (oder stark vereinfacht) */}
                 {/* Beispiel: Einfache Filter für Farbe und Kategorie, falls erlaubt */}
                <div className="simple-filters guest-filters">
                    <div className="filter-item">
                        <label htmlFor="kategorie">Kategorie:</label>
                        <select id="kategorie" name="kategorie" value={currentParams.kategorie} onChange={handleFilterChange}>
                            <option value="">Alle Kategorien</option>
                            {KATEGORIEN.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                    </div>
                    {farbenOptions.length > 0 && (
                        <div className="filter-item">
                            <label htmlFor="farbe">Farbe:</label>
                            <select id="farbe" name="farbe" value={currentParams.farbe} onChange={handleFilterChange}>
                                <option value="">Alle Farben</option> {farbenOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="filter-buttons">
                        <button type="button" onClick={handleResetFilters} className="btn btn-reset">Filter zurücksetzen</button>
                    </div>
                </div>
            </section>

            <hr className="section-divider" />

            {/* Sorting Controls (ggf. reduzierte Optionen für Gäste) */}
            <div className="sort-controls">
                <label htmlFor="sortBy">Sortieren nach:</label>
                <select id="sortBy" value={currentParams.sortBy} onChange={handleSortChange}>
                    {/* Reduzierte Liste basierend auf typischen erlaubten Attributen für Gäste */}
                    {/* Alternativ: Dynamisch basierend auf den tatsächlich vorhandenen Attributen im ersten Stein */}
                    <option value="name">Name</option>
                    {aktuelleSteine.length > 0 && aktuelleSteine[0]?.hasOwnProperty('kategorie') && <option value="kategorie">Kategorie</option>}
                    {aktuelleSteine.length > 0 && aktuelleSteine[0]?.hasOwnProperty('status') && <option value="status">Status</option>}
                    {aktuelleSteine.length > 0 && aktuelleSteine[0]?.hasOwnProperty('gewicht') && <option value="gewicht">Gewicht</option>}
                    {/* Weitere Optionen hier hinzufügen, wenn sie häufig für Gäste erlaubt sind */}
                </select>
                <button onClick={toggleSortOrder} className={`sort-order-button ${currentParams.sortOrder === 'asc' ? 'asc' : 'desc'}`} disabled={!currentParams.sortBy} aria-label={`Sortierrichtung umschalten, aktuell ${currentParams.sortOrder === 'asc' ? 'aufsteigend' : 'absteigend'}`} >
                    {currentParams.sortOrder === 'asc' ? (<>Auf <FontAwesomeIcon icon={faSortUp} /></>) : (<>Ab <FontAwesomeIcon icon={faSortDown} /></>)}
                </button>
            </div>

            <hr className="section-divider" />

            <section id="gast-stein-liste-anker" className="stones-list-section">
                <div className="stones-header">
                    {/* Kein "Neuen Stein hinzufügen" Button für Gäste */}
                    <div className="stones-count">
                       {isLoading ? ( <p>Lade Steine...</p> ) : error ? null :
                         ( itemsPerPage === 0 ? ( <p>Zeige alle {totalSteine} Steine</p> ) : ( <p> Zeige {aktuelleSteine.length} von {totalSteine} Steinen {gesamtSeiten > 1 && ` (Seite ${currentPage} von ${gesamtSeiten})`} </p> ) )
                       }
                    </div>
                </div>

                 {isLoading && !error && ( <div className="loading-indicator"><p>Lade Steine...</p></div> )}
                 {!isLoading && error && (
                    <div className="error-message"> <p>{error}</p>
                        {!(error.includes("anmelden") || error.includes("Token")) && <button onClick={() => fetchSteine(currentParams)} className="btn btn-retry"> Erneut versuchen </button> }
                    </div>
                 )}
                 {!isLoading && !error && aktuelleSteine.length === 0 && ( <p>Keine Steine gefunden, die den Kriterien entsprechen oder für Sie freigegeben sind.</p> )}

                 {!isLoading && !error && aktuelleSteine.length > 0 && (
                    <>
                        <div className="stein-grid">
                            {aktuelleSteine.map((stein) => (
                                <div key={stein._id} 
                                     className={`stein-card status-${stein.status ? stein.status.replace(/\s+/g, '-').toLowerCase() : 'unbekannt'}`} // Status-Klasse beibehalten, falls Status angezeigt wird
                                    onClick={(e) => handleCardClick(e, stein)} role="button" tabIndex={0} 
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(e, stein); }} 
                                    aria-label={`Details für ${stein.name || 'Unbenannt'} anzeigen`} >
                                    
                                    {/* Bild nur zeigen, wenn bildPfad ein erlaubtes Attribut des Gastes ist */}
                                    {(stein.bildPfad || stein.hasOwnProperty('bildPfad')) && (
                                        <SteinBild
                                            bildPfad={stein.bildPfad}
                                            size="card"
                                            onClick={(e) => handleImageClick(e, stein)}
                                            alt={stein.name || 'Steinbild'}
                                        />
                                    )}

                                    <div className="stein-details">
                                        {/* Name immer anzeigen, da es das primäre Identifikationsmerkmal ist */}
                                        <h3 className="stein-name">{stein.name || 'Unbenannt'}</h3>

                                        {/* Explizit definierte Attribute für die Kartenansicht, die auch in SteinListe.js verwendet werden (ohne Notizen) */}
                                        {/* Backend stellt sicher, dass nur erlaubte Attribute im 'stein' Objekt sind */}
                                        {[
                                            'kennzeichen', 'kategorie', 'merkmal', 'status', 'farbe', 'gewicht',
                                            'laenge', 'breite', 'tiefe', 'dicke', 'durchmesser',
                                            'preisProGramm', 'kaufPreis', 'verkaufPreis', 'serienBezeichnung'
                                        ].map(key => {
                                            if (!stein.hasOwnProperty(key) || stein[key] === null || stein[key] === undefined || String(stein[key]).trim() === '') return null;
                                            const labelMap = {
                                                kennzeichen: 'Kz.:', kategorie: 'Kat.:', merkmal: 'Merkmal:',
                                                status: 'Status:', gewicht: 'Gewicht:',
                                                laenge: 'Länge:', breite: 'Breite:', tiefe: 'Tiefe:', dicke: 'Dicke:',
                                                durchmesser: 'Durchm.:', preisProGramm: '€/g:',
                                                kaufPreis: 'Kauf:', verkaufPreis: 'Verkauf:',
                                                serienBezeichnung: 'Serie:', farbe: 'Farbe:',
                                            };
                                            const displayLabel = labelMap[key] || (key.charAt(0).toUpperCase() + key.slice(1) + ':');
                                            let displayValue = stein[key];
                                            if (key === 'gewicht' && typeof stein[key] === 'number') displayValue = `${stein[key]} g`;
                                            else if (['laenge', 'breite', 'tiefe', 'dicke', 'durchmesser'].includes(key) && typeof stein[key] === 'number') displayValue = `${stein[key]} mm`;
                                            else if (['kaufPreis', 'verkaufPreis', 'preisProGramm'].includes(key) && typeof stein[key] === 'number') displayValue = `€ ${stein[key]}`;
                                            return (
                                                <p key={key} className="stein-property">
                                                    <span className="property-label">{displayLabel}</span> {String(displayValue)}
                                                </p>
                                            );
                                        })}
                                        {stein.aufGlas && (
                                            <p className="stein-property"><span className="property-label">Montierung:</span> Auf Glas</p>
                                        )}
                                    </div>
                                    {/* Keine Bearbeiten/Löschen Buttons für Gäste */}
                                </div>
                            ))}
                        </div>

                         {gesamtSeiten > 1 && itemsPerPage !== 0 && (
                            <div className="pagination">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="pagination-button"> <FontAwesomeIcon icon={faChevronLeft} /> Zurück </button>
                                <span className="pagination-info" aria-live="polite" aria-atomic="true"> Seite {currentPage} von {gesamtSeiten} </span>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === gesamtSeiten || isLoading} className="pagination-button"> Weiter <FontAwesomeIcon icon={faChevronRight} /> </button>
                            </div>
                        )}

                        <div className="items-per-page">
                            <label htmlFor="itemsPerPage">Steine pro Seite:</label>
                            <select id="itemsPerPage" value={currentParams.limit} onChange={handleItemsPerPageChange} disabled={isLoading} >
                                <option value="6">6</option> <option value="12">12</option> <option value="24">24</option> <option value="48">48</option> <option value="96">96</option> <option value="0">Alle</option>
                            </select>
                        </div>
                    </>
                 )}
            </section>

            {showModal && selectedImage && ( <Modal onClose={() => setShowModal(false)}> <img src={selectedImage} alt="Stein Großansicht" className="modal-image" /> </Modal> )}
        </main>
    );
}

export default GastSteinListe; 